import {
  DestroyRef,
  inject,
  Injectable,
  PLATFORM_ID,
  Signal,
  computed,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  map,
  of,
  catchError,
  startWith,
  distinctUntilChanged,
  shareReplay,
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import {
  ScreenSizeInfo,
  ScreenState,
  ResponsiveConfig,
} from './screen-size-info.interface';
import { STANDARD_BREAKPOINTS, DEVICE_BREAKPOINTS } from './screen.config';

/**
 * Servicio standalone para gestión de responsive design y detección de dispositivos.
 * Utiliza los breakpoints estándar de Angular CDK para máxima compatibilidad.
 *
 * No requiere módulo específico - se auto-configura al ser inyectado.
 * Compatible con standalone components y la arquitectura moderna de Angular.
 *
 * @example
 * ```typescript
 * // En standalone component
 * @Component({
 *   standalone: true,
 *   imports: [CommonModule],
 *   // No necesita providers adicionales, usa providedIn: 'root'
 * })
 * export class MyComponent {
 *   constructor(private screenService: ScreenService) {}
 *
 *   // Usando signals (recomendado)
 *   isMobile = this.screenService.isMobile();
 *   config = this.screenService.responsiveConfig();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ScreenService {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Observable principal para información de pantalla
   * Utiliza todos los breakpoints estándar de Angular CDK
   */
  readonly screenInfo$ = isPlatformBrowser(this.platformId)
    ? this.breakpointObserver
        .observe([
          STANDARD_BREAKPOINTS.mobile,
          STANDARD_BREAKPOINTS.tablet,
          STANDARD_BREAKPOINTS.desktop,
          STANDARD_BREAKPOINTS.large,
          STANDARD_BREAKPOINTS.xlarge,
          DEVICE_BREAKPOINTS.handsetPortrait,
          DEVICE_BREAKPOINTS.handsetLandscape,
          DEVICE_BREAKPOINTS.tabletPortrait,
          DEVICE_BREAKPOINTS.tabletLandscape,
          DEVICE_BREAKPOINTS.webPortrait,
          DEVICE_BREAKPOINTS.webLandscape,
        ])
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(({ breakpoints }) =>
            this.mapBreakpointsToScreenInfo(breakpoints)
          ),
          distinctUntilChanged((prev, curr) =>
            this.compareScreenInfo(prev, curr)
          ),
          startWith(this.getInitialScreenInfo()),
          catchError((error) => {
            console.error(
              '[ScreenService] Error observing breakpoints:',
              error
            );
            return of(this.getInitialScreenInfo());
          }),
          shareReplay(1)
        )
    : of(this.getInitialScreenInfo());

  /**
   * Signal reactivo para información de pantalla
   */
  readonly screenInfo: Signal<ScreenSizeInfo> = toSignal(this.screenInfo$, {
    initialValue: this.getInitialScreenInfo(),
  });

  /**
   * Signals computados para acceso directo a propiedades específicas
   */
  readonly isMobile = computed(() => this.screenInfo().isMobile);
  readonly isTablet = computed(() => this.screenInfo().isTablet);
  readonly isDesktop = computed(() => this.screenInfo().isDesktop);
  readonly isLarge = computed(() => this.screenInfo().isLarge);
  readonly isXLarge = computed(() => this.screenInfo().isXLarge);
  readonly isPortrait = computed(() => this.screenInfo().isPortrait);
  readonly isLandscape = computed(() => this.screenInfo().isLandscape);
  readonly currentBreakpoint = computed(
    () => this.screenInfo().currentBreakpoint
  );

  /**
   * Signal computado para estado de pantalla
   */
  readonly screenState = computed((): ScreenState => {
    const info = this.screenInfo();
    if (info.isMobile) return 'mobile';
    if (info.isTablet) return 'tablet';
    if (info.isDesktop) return 'desktop';
    if (info.isLarge) return 'large';
    return 'xlarge';
  });

  /**
   * Signal computado para configuración responsiva automática
   */
  readonly responsiveConfig = computed((): ResponsiveConfig => {
    const state = this.screenState();
    const info = this.screenInfo();

    switch (state) {
      case 'mobile':
        return {
          showSidebar: false,
          columnsCount: 1,
          showFilters: false,
          showAdvancedFeatures: false,
          buttonSize: 'large',
          navigationStyle: 'bottom',
        };
      case 'tablet':
        return {
          showSidebar: !info.isPortrait,
          columnsCount: info.isPortrait ? 2 : 3,
          showFilters: true,
          showAdvancedFeatures: false,
          buttonSize: 'medium',
          navigationStyle: info.isPortrait ? 'top' : 'sidebar',
        };
      case 'desktop':
        return {
          showSidebar: true,
          columnsCount: 4,
          showFilters: true,
          showAdvancedFeatures: true,
          buttonSize: 'medium',
          navigationStyle: 'sidebar',
        };
      case 'large':
        return {
          showSidebar: true,
          columnsCount: 5,
          showFilters: true,
          showAdvancedFeatures: true,
          buttonSize: 'medium',
          navigationStyle: 'sidebar',
        };
      case 'xlarge':
        return {
          showSidebar: true,
          columnsCount: 6,
          showFilters: true,
          showAdvancedFeatures: true,
          buttonSize: 'large',
          navigationStyle: 'sidebar',
        };
    }
  });

  /**
   * Mapea los breakpoints de Angular CDK a información de pantalla
   */
  private mapBreakpointsToScreenInfo(breakpoints: {
    [key: string]: boolean;
  }): ScreenSizeInfo {
    const isMobile = breakpoints[STANDARD_BREAKPOINTS.mobile];
    const isTablet = breakpoints[STANDARD_BREAKPOINTS.tablet];
    const isDesktop = breakpoints[STANDARD_BREAKPOINTS.desktop];
    const isLarge = breakpoints[STANDARD_BREAKPOINTS.large];
    const isXLarge = breakpoints[STANDARD_BREAKPOINTS.xlarge];

    // Determinar orientación usando los breakpoints específicos de dispositivos
    const isPortrait =
      breakpoints[DEVICE_BREAKPOINTS.handsetPortrait] ||
      breakpoints[DEVICE_BREAKPOINTS.tabletPortrait] ||
      breakpoints[DEVICE_BREAKPOINTS.webPortrait];

    const isLandscape =
      breakpoints[DEVICE_BREAKPOINTS.handsetLandscape] ||
      breakpoints[DEVICE_BREAKPOINTS.tabletLandscape] ||
      breakpoints[DEVICE_BREAKPOINTS.webLandscape];

    // Obtener dimensiones actuales (solo en browser)
    const screenWidth = isPlatformBrowser(this.platformId)
      ? window.innerWidth
      : 0;
    const screenHeight = isPlatformBrowser(this.platformId)
      ? window.innerHeight
      : 0;
    const devicePixelRatio = isPlatformBrowser(this.platformId)
      ? window.devicePixelRatio
      : 1;

    // Determinar breakpoint actual
    let currentBreakpoint = 'unknown';
    if (isMobile) currentBreakpoint = 'mobile';
    else if (isTablet) currentBreakpoint = 'tablet';
    else if (isDesktop) currentBreakpoint = 'desktop';
    else if (isLarge) currentBreakpoint = 'large';
    else if (isXLarge) currentBreakpoint = 'xlarge';

    return {
      isMobile,
      isTablet,
      isDesktop,
      isLarge,
      isXLarge,
      isPortrait,
      isLandscape,
      screenWidth,
      screenHeight,
      devicePixelRatio,
      currentBreakpoint,
    };
  }

  /**
   * Proporciona información inicial de pantalla para SSR
   * Default a desktop para mejor experiencia inicial
   */
  private getInitialScreenInfo(): ScreenSizeInfo {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true, // Default sensato para SSR
      isLarge: false,
      isXLarge: false,
      isPortrait: false,
      isLandscape: false,
      screenWidth: 0,
      screenHeight: 0,
      devicePixelRatio: 1,
      currentBreakpoint: 'desktop',
    };
  }

  /**
   * Compara dos estados de pantalla para optimizar re-renders
   */
  private compareScreenInfo(
    prev: ScreenSizeInfo,
    curr: ScreenSizeInfo
  ): boolean {
    return (
      prev.isMobile === curr.isMobile &&
      prev.isTablet === curr.isTablet &&
      prev.isDesktop === curr.isDesktop &&
      prev.isLarge === curr.isLarge &&
      prev.isXLarge === curr.isXLarge &&
      prev.isPortrait === curr.isPortrait &&
      prev.isLandscape === curr.isLandscape
    );
  }

  /**
   * Métodos de utilidad
   */

  /**
   * Obtiene la configuración responsiva actual
   */
  getResponsiveConfig(): ResponsiveConfig {
    return this.responsiveConfig();
  }

  /**
   * Verifica si el dispositivo tiene alta densidad de píxeles
   */
  isHighDensity(): boolean {
    return isPlatformBrowser(this.platformId) && window.devicePixelRatio > 1.5;
  }

  /**
   * Método para debug y desarrollo
   */
  getDebugInfo(): {
    screenInfo: ScreenSizeInfo;
    responsiveConfig: ResponsiveConfig;
  } {
    return {
      screenInfo: this.screenInfo(),
      responsiveConfig: this.responsiveConfig(),
    };
  }

  /**
   * Métodos de compatibilidad para migración gradual
   * @deprecated Use signals instead
   */
  get isMobileValue(): boolean {
    return this.isMobile();
  }
  get isTabletValue(): boolean {
    return this.isTablet();
  }
  get isDesktopValue(): boolean {
    return this.isDesktop();
  }
}
